import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ICHING_TRIGRAMS } from '../core/iching-trigrams.const';
import { IChingTrigramId } from '../core/iching.models';

interface TrigramRow { position: 1 | 2 | 3; yang: boolean; y: number; }

/**
 * Trigramfiguur: drie lijnen onder→boven (lijn 1 onderaan), yin gebroken,
 * yang heel; dezelfde stijl als de hexagramfiguur. `tone` kleurt de lijnen
 * via dezelfde CSS-variabelen als de hexagramfiguur: `lower`
 * (`--hd-iching-lower`) en `highlight` (`--hd-iching-highlight`) standaard in
 * `--primary`, `upper` (`--hd-iching-upper`) standaard in `--amber`. De maat
 * staat op de host (`size`; `normal` = 64px, even breed als de hexagramfiguur,
 * dus met even brede lijnen; `medium` = 36px voor een figuur in een tekstregel;
 * `mini` = 24px), de svg vult die breedte.
 */
@Component({
  selector: 'hd-iching-trigram-figure',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': '"hd-iching-trigram-figure hd-iching-trigram-figure--" + size() + " hd-iching-trigram-figure--tone-" + tone()',
  },
  styles: `
    :host { display: inline-block; flex: 0 0 auto; line-height: 0; width: 64px; }
    :host(.hd-iching-trigram-figure--medium) { width: 36px; }
    :host(.hd-iching-trigram-figure--mini) { width: 24px; }
    svg { display: block; width: 100%; height: auto; }
    .line { fill: var(--hd-iching-line, var(--text-primary, currentColor)); }
    :host(.hd-iching-trigram-figure--tone-lower) .line { fill: var(--hd-iching-lower, var(--primary)); }
    :host(.hd-iching-trigram-figure--tone-upper) .line { fill: var(--hd-iching-upper, var(--amber)); }
    :host(.hd-iching-trigram-figure--tone-highlight) .line { fill: var(--hd-iching-highlight, var(--primary)); }
  `,
  template: `
    <svg viewBox="0 0 100 44" [attr.aria-label]="ariaLabel() || undefined" role="img">
      @for (row of rows(); track row.position) {
        <g [attr.data-line]="row.position" [class.is-yang]="row.yang" [class.is-yin]="!row.yang">
          @if (row.yang) {
            <rect class="line" x="10" [attr.y]="row.y" width="80" height="8" rx="1" />
          } @else {
            <rect class="line" x="10" [attr.y]="row.y" width="34" height="8" rx="1" />
            <rect class="line" x="56" [attr.y]="row.y" width="34" height="8" rx="1" />
          }
        </g>
      }
    </svg>
  `,
})
export class HdIChingTrigramFigureComponent {
  readonly trigram = input.required<IChingTrigramId>();
  readonly size = input<'mini' | 'medium' | 'normal'>('normal');
  readonly tone = input<'default' | 'lower' | 'upper' | 'highlight'>('default');
  readonly ariaLabel = input<string>('');

  protected readonly rows = computed<TrigramRow[]>(() =>
    [...ICHING_TRIGRAMS[this.trigram()].bits].map((bit, i) => ({
      position: (i + 1) as 1 | 2 | 3,
      yang: bit === '1',
      y: 36 - i * 16,
    })),
  );
}
